import React, { useState } from 'react';
import { X, Upload, Image as ImageIcon } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useCT } from '../utils/creationTranslations';
import { useKeyboardHeight } from '../hooks/useKeyboardHeight';
import MonetizationInfoBubble from './MonetizationInfoBubble';

const PodcastUploader = ({ onClose, onSuccess }) => {
  const ct = useCT();
  const keyboardHeight = useKeyboardHeight();
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: '',
    guests: ''
  });
  const [audioFile, setAudioFile] = useState(null);
  const [coverFile, setCoverFile] = useState(null);
  const [coverPreview, setCoverPreview] = useState(null);
  const [uploading, setUploading] = useState(false);

  const categories = [
    { key: 'catCinema', value: 'Cinéma' },
    { key: 'catSeries', value: 'Séries' },
    { key: 'catMaking', value: 'Making-of' },
    { key: 'catTech', value: 'Tech' },
    { key: 'catMusic', value: 'Musiques de films' },
    { key: 'catInterviews', value: 'Interviews' },
    { key: 'catReviews', value: 'Critiques' },
  ];

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleAudioChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setAudioFile(file);
    }
  };

  const handleCoverChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setCoverFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setCoverPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleUpload = async () => {
    if (!audioFile) {
      toast.error(ct('pod.errNoAudio'));
      return;
    }

    if (!formData.title) {
      toast.error(ct('pod.errNoTitle'));
      return;
    }

    setUploading(true);

    try {
      const data = new FormData();
      data.append('audio_file', audioFile);
      data.append('title', formData.title);
      if (formData.description) data.append('description', formData.description);
      if (formData.category) data.append('category', formData.category);
      if (formData.guests) data.append('guests', formData.guests);
      if (coverFile) data.append('cover_image', coverFile);

      await axios.post('/api/podcasts', data, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      onSuccess();
    } catch (error) {
      toast.error(ct('pod.errUpload'));
      console.error('Upload error:', error);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center p-4 pb-0 bg-black/80 backdrop-blur-sm overflow-hidden" data-testid="podcast-uploader-modal">
      <div
        className="relative w-full max-w-2xl bg-gray-900 rounded-3xl shadow-2xl border border-white/10 my-4 overflow-hidden"
        style={{ maxHeight: `calc(100dvh - 2rem - ${keyboardHeight}px)`, display: 'flex', flexDirection: 'column' }}
      >
        {/* Close button - Sticky */}
        <button
          data-testid="close-uploader-btn"
          onClick={onClose}
          className="absolute top-3 right-3 p-2 hover:bg-white/10 rounded-full transition-colors z-20 bg-gray-900/90 backdrop-blur-sm"
        >
          <X className="w-5 h-5" />
        </button>

        <div
          className="p-8 pt-12 overflow-y-auto overscroll-contain flex-1"
          style={{ paddingBottom: `calc(env(safe-area-inset-bottom, 0px) + ${keyboardHeight > 0 ? keyboardHeight + 32 : 64}px)`, WebkitOverflowScrolling: 'touch' }}
          onFocus={(e) => {
            // When any focusable child gets focus (input/textarea/contenteditable),
            // gently scroll it into view to avoid the on-screen keyboard hiding it.
            const tag = e.target?.tagName;
            if (tag === 'INPUT' || tag === 'TEXTAREA' || e.target?.isContentEditable) {
              setTimeout(() => {
                try { e.target.scrollIntoView({ behavior: 'smooth', block: 'center' }); } catch (_e) { /* ignore scroll errors */ }
              }, 200);
            }
          }}
        >

        <h2 className="text-3xl font-bold mb-6">{ct('pod.modalTitle')}</h2>

        <div className="space-y-6">
          {/* Cover Image */}
          <div>
            <label className="block text-sm font-medium mb-2">{ct('pod.cover')}</label>
            <div className="flex items-center space-x-4">
              {coverPreview ? (
                <div className="relative w-32 h-32 rounded-xl overflow-hidden">
                  <img src={coverPreview} alt="Cover" className="w-full h-full object-cover" />
                  <button
                    onClick={() => {
                      setCoverFile(null);
                      setCoverPreview(null);
                    }}
                    className="absolute top-2 right-2 p-1 bg-black/60 hover:bg-black/80 rounded-full transition-colors"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <label className="w-32 h-32 flex flex-col items-center justify-center border-2 border-dashed border-white/20 rounded-xl hover:border-magenta-500/50 cursor-pointer transition-colors">
                  <ImageIcon className="w-8 h-8 text-gray-500 mb-2" />
                  <span className="text-xs text-gray-500">Ajouter</span>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleCoverChange}
                    className="hidden"
                  />
                </label>
              )}
            </div>
          </div>

          {/* Title */}
          <div>
            <label htmlFor="title" className="block text-sm font-medium mb-2">
              {ct('pod.title')}
            </label>
            <input
              data-testid="podcast-title-input"
              type="text"
              id="title"
              name="title"
              value={formData.title}
              onChange={handleChange}
              required
              className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl focus:outline-none focus:ring-2 focus:ring-magenta-500 transition-all"
              placeholder={ct('pod.titlePlaceholder')}
            />
          </div>

          {/* Category */}
          <div>
            <label htmlFor="category" className="block text-sm font-medium mb-2">
              {ct('pod.category')}
            </label>
            <select
              data-testid="podcast-category-select"
              id="category"
              name="category"
              value={formData.category}
              onChange={handleChange}
              className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl focus:outline-none focus:ring-2 focus:ring-magenta-500 transition-all text-white [&>option]:bg-gray-800 [&>option]:text-white"
            >
              <option value="" className="bg-gray-800 text-white">{ct('pod.selectCategory')}</option>
              {categories.map((cat) => (
                <option key={cat.value} value={cat.value} className="bg-gray-800 text-white">{ct(`pod.${cat.key}`)}</option>
              ))}
            </select>
          </div>

          {/* Guests */}
          <div>
            <label htmlFor="guests" className="block text-sm font-medium mb-2">
              {ct('pod.guests')}
            </label>
            <input
              data-testid="podcast-guests-input"
              type="text"
              id="guests"
              name="guests"
              value={formData.guests}
              onChange={handleChange}
              className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl focus:outline-none focus:ring-2 focus:ring-magenta-500 transition-all"
              placeholder={ct('pod.guestsPlaceholder')}
            />
          </div>

          {/* Audio File */}
          <div>
            <label className="block text-sm font-medium mb-2">{ct('pod.audioFile')}</label>
            <label className="flex items-center justify-center w-full px-4 py-8 border-2 border-dashed border-white/20 rounded-xl hover:border-magenta-500/50 cursor-pointer transition-colors">
              <div className="text-center">
                <Upload className="w-8 h-8 mx-auto mb-2 text-gray-500" />
                {audioFile ? (
                  <div>
                    <p className="text-sm font-medium text-magenta-400">{audioFile.name}</p>
                    <p className="text-xs text-gray-500 mt-1">
                      {(audioFile.size / 1024 / 1024).toFixed(2)} MB
                    </p>
                  </div>
                ) : (
                  <div>
                    <p className="text-sm text-gray-400">{ct('pod.pickFile')}</p>
                    <p className="text-xs text-gray-500 mt-1">{ct('pod.audioFormats')}</p>
                  </div>
                )}
              </div>
              <input
                data-testid="podcast-audio-input"
                type="file"
                accept="audio/*"
                onChange={handleAudioChange}
                className="hidden"
              />
            </label>
          </div>

          <MonetizationInfoBubble />

          {/* Upload Button */}
          <button
            data-testid="upload-podcast-submit-btn"
            onClick={handleUpload}
            disabled={uploading}
            className="w-full py-4 bg-gradient-to-r from-magenta-500 to-violet-600 hover:from-magenta-600 hover:to-violet-700 rounded-xl font-semibold transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
          >
            <Upload className="w-5 h-5" />
            <span>{uploading ? '...' : ct('pod.publish')}</span>
          </button>
        </div>
        </div>
      </div>
    </div>
  );
};

export default PodcastUploader;
